import { motion } from "framer-motion";
import { AlertCircle, AlertTriangle, Bug, Info, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { ErrorLog, useErrorStore } from "../stores/errorStore";

export const Debug = () => {
  const { errors, clearErrors, removeError } = useErrorStore();
  const [filter, setFilter] = useState<"all" | "error" | "warning" | "info">("all");

  const filteredErrors = errors.filter((error) => filter === "all" || error.type === filter);

  const getErrorIcon = (type: ErrorLog["type"]) => {
    switch (type) {
      case "error":
        return <AlertCircle className="text-red-500 w-5 h-5" />;
      case "warning":
        return <AlertTriangle className="text-yellow-500 w-5 h-5" />;
      case "info":
        return <Info className="text-blue-500 w-5 h-5" />;
    }
  };

  const renderErrorDetails = (error: ErrorLog) => (
    <motion.div
      key={error.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-800 rounded-lg p-4 mb-4 relative"
    >
      <div className="flex items-center gap-3 mb-2">
        {getErrorIcon(error.type)}
        <span className="font-semibold text-zinc-200 capitalize">{error.type}</span>
        <span className="text-zinc-400 text-sm ml-auto">{new Date(error.timestamp).toLocaleString()}</span>
        <button onClick={() => removeError(error.id)} className="text-zinc-400 hover:text-red-500 transition">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="text-zinc-300">{error.message}</div>
      {error.stack && <pre className="text-xs text-zinc-500 mt-2 overflow-x-auto">{error.stack}</pre>}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-black p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Bug className="w-8 h-8 text-yellow-500" />
            <h1 className="text-3xl font-bold">Debug Console</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")}>
                All
              </Button>
              <Button
                variant={filter === "error" ? "destructive" : "outline"}
                size="sm"
                onClick={() => setFilter("error")}
              >
                Errors
              </Button>
              <Button
                variant={filter === "warning" ? "secondary" : "outline"}
                size="sm"
                onClick={() => setFilter("warning")}
              >
                Warnings
              </Button>
              <Button variant={filter === "info" ? "default" : "outline"} size="sm" onClick={() => setFilter("info")}>
                Info
              </Button>
            </div>
            <Button variant="outline" size="icon" onClick={() => window.location.reload()} title="Refresh Page">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="destructive" onClick={clearErrors} disabled={errors.length === 0}>
              <Trash2 className="w-4 h-4 mr-2" /> Clear All
            </Button>
          </div>
        </div>

        {filteredErrors.length === 0 ? (
          <div className="text-center text-zinc-400 py-12">
            <Bug className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
            <p>No errors or logs to display</p>
          </div>
        ) : (
          <div className="space-y-4">{filteredErrors.map(renderErrorDetails)}</div>
        )}
      </div>
    </div>
  );
};
